// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Generic error boundary for catching 404 errors on entity detail routes.
 * Use this to wrap route content that fetches a single entity by ID.
 */
import { EntityNotFound } from '@/components/entity-not-found';
import { LinkProps } from '@tanstack/react-router';
import axios from 'axios';
import { PropsWithChildren } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

interface Props extends PropsWithChildren {
  entityType: string;
  /** Type-safe route path - must be a valid route from the route tree. */
  listPath: LinkProps['to'];
  listLabel: string;
}

export function NotFoundErrorBoundary(props: Props) {
  const handleError = (error: Error) => {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return (
        <EntityNotFound
          entityType={props.entityType}
          listPath={props.listPath}
          listLabel={props.listLabel}
        />
      );
    }

    throw error;
  };

  return (
    <ErrorBoundary
      fallbackRender={({ error }) => handleError(error)}
      onError={(error) => {
        if (!axios.isAxiosError(error) || error.response?.status !== 404) {
          throw error;
        }
      }}
    >
      {props.children}
    </ErrorBoundary>
  );
}
